import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  BarChart3, FolderOpen, Star, ClipboardList, Diamond, Mail, Phone, MessageSquare,
  CalendarCheck, MapPin, FileText, FlaskConical, Coins, FileSignature, XCircle, Trophy, Hourglass, Circle,
} from "lucide-react";

const iconFor = (label) => {
  const L = (label || "").toLocaleUpperCase("tr-TR");
  if (L.includes("TOPLAM")) return BarChart3;
  if (L.includes("PORTF")) return FolderOpen;
  if (L.includes("ÖZEL")) return Star;
  if (L.includes("AŞAMASIZ")) return ClipboardList;
  if (L.includes("AVM")) return Diamond;
  if (L.includes("MAIL")) return Mail;
  if (L.includes("ARAMA")) return Phone;
  if (L.includes("MESAJ")) return MessageSquare;
  if (L.includes("RANDEVU")) return CalendarCheck;
  if (L.includes("ZİYARET")) return MapPin;
  if (L.includes("SÖZLEŞME")) return FileSignature;
  if (L.includes("TEKLİF")) return FileText;
  if (L.includes("DENEME")) return FlaskConical;
  if (L.includes("FİYAT")) return Coins;
  if (L.includes("KAYBED")) return XCircle;
  if (L.includes("KAZAN")) return Trophy;
  if (L.includes("DEVAM")) return Hourglass;
  return Circle;
};

const Item = ({ label, value, color, highlight }) => {
  const Icon = iconFor(label);
  return (
    <div className={`flex min-w-0 flex-col items-center rounded-md px-1 py-1 text-center ${highlight ? "bg-blue-500/10 ring-1 ring-blue-500/30" : ""}`} style={{ flex: "1 1 0" }}>
      <Icon className="mb-0.5 h-4 w-4 shrink-0" style={{ color }} />
      <div className="font-head text-base font-extrabold leading-none text-white">{value}</div>
      <div className="mt-0.5 w-full truncate text-[9px] font-semibold leading-tight text-slate-400" title={label}>{label}</div>
    </div>
  );
};

export default function TopReport({ items }) {
  const { columns } = useAuth();
  const [ov, setOv] = useState(null);

  useEffect(() => { if (items) return; api.get("/reports/overview", { silent: true }).then((r) => setOv(r.data)); }, [items]);

  const computed = useMemo(() => {
    if (!items) return null;
    const cnt = (key) => { const m = {}; items.forEach((it) => { const v = it.data?.[key]; if (v) m[v] = (m[v] || 0) + 1; }); return m; };
    const stageless = items.filter((it) => { const d = it.data || {}; return !d.ilk_temas && !d.asama1 && !d.asama2 && !d.asama3 && !d.sonuc; }).length;
    return { total: items.length, kategori: cnt("kategori"), asamasiz: stageless, ilk_temas: cnt("ilk_temas"), asama1: cnt("asama1"), asama2: cnt("asama2"), asama3: cnt("asama3"), sonuc: cnt("sonuc") };
  }, [items]);

  const data = items ? computed : ov;
  if (!data) return null;

  const col = (k) => columns.find((c) => c.key === k) || {};
  const opts = (k) => col(k).options || [];
  const cmap = (k) => col(k).colors || {};

  const sections = [
    { title: "GENEL", items: [
      { label: "TOPLAM", value: data.total, color: "#3B82F6", highlight: true },
      ...opts("kategori").map((o) => ({ label: o === "ÖZEL" ? "ÖZEL MÜŞTERİ" : o, value: data.kategori[o] || 0, color: cmap("kategori")[o] || "#64748B" })),
      { label: "AŞAMASIZ", value: data.asamasiz, color: "#94A3B8" },
    ]},
    { title: "İLK TEMAS", items: opts("ilk_temas").map((o) => ({ label: o, value: (data.ilk_temas || {})[o] || 0, color: cmap("ilk_temas")[o] || "#8B5CF6" })) },
    { title: "1. AŞAMA", items: opts("asama1").map((o) => ({ label: o, value: (data.asama1 || {})[o] || 0, color: cmap("asama1")[o] || "#F59E0B" })) },
    { title: "2. AŞAMA", items: opts("asama2").map((o) => ({ label: o, value: (data.asama2 || {})[o] || 0, color: cmap("asama2")[o] || "#06B6D4" })) },
    { title: "3. AŞAMA", items: opts("asama3").map((o) => ({ label: o, value: (data.asama3 || {})[o] || 0, color: cmap("asama3")[o] || "#10B981" })) },
    { title: "SONUÇ", items: opts("sonuc").map((o) => ({ label: o, value: (data.sonuc || {})[o] || 0, color: cmap("sonuc")[o] || "#EF4444" })) },
  ];

  const weight = (s) => Math.max(1, s.items.length);

  return (
    <div data-testid="top-report" className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900 p-2">
      <div className="flex w-full flex-nowrap items-stretch gap-1">
        {sections.map((s, si) => (
          <div key={s.title} className="flex min-w-0 items-stretch" style={{ flex: `${weight(s)} 1 0` }}>
            <div className="min-w-0 flex-1 px-1">
              <div className="mb-1 truncate text-center text-[10px] font-bold uppercase tracking-wider text-blue-300/80">{s.title}</div>
              <div className="flex flex-nowrap gap-0.5">
                {s.items.length === 0 ? <span className="w-full text-center text-xs text-slate-600">—</span> :
                  s.items.map((it) => <Item key={it.label} {...it} />)}
              </div>
            </div>
            {si < sections.length - 1 && <div className="w-px shrink-0 bg-slate-800" />}
          </div>
        ))}
      </div>
    </div>
  );
}
